import { useTranslation } from "react-i18next";
import {
	ChartCard,
	ColumnChart,
	Donut,
	HBarList,
	type HolatRangi,
	holatVar,
	Legend,
} from "@/shared/charts";
import type { AnalyticsLayoutProps } from "../../../types";
import { KpiRow, sonMatni } from "../../detail/KpiRow";
import {
	BolimlarRoyxati,
	kategoriyaRangi,
	kunlikUstunlar,
	type Qatlam,
	segmentliQatorlar,
	TOP_N,
} from "./shared";

/**
 * Topshiriqlar analitikasi (reja E3).
 *
 * Tartib: KPI → [kunlar (yaratilgan) | holatlar donut'i] →
 * [ijrochilar (muddati o'tgan segment) | bo'limlar].
 *
 * ⚠️ `flagged` (muddati o'tgan FAOL) — `primary` ning QISMI (`shared.tsx`).
 * ⚠️ Backend `heat`/`hourly`/`byZone` ni bu kind uchun to'ldirmaydi —
 * ularning kartasi YO'Q (bo'sh karta chizilmaydi).
 * ⚠️ Ijrochilar ro'yxati IJROCHI bo'yicha, bo'limlar esa topshiriq
 * EGASI bo'limi bo'yicha — ikki ro'yxat yig'indisi mos kelmaydi
 * (backend `tasks.ts` qarori), shuning uchun umumiy izoh yozilmaydi.
 */

const RANG = {
	oddiy: holatVar("kelgan"),
	muddatiOtgan: holatVar("kelmagan"),
} as const;

const HOLAT_RANGI: Record<string, HolatRangi> = {
	faol: "kelgan",
	yakunlangan: "uzrli",
	// biome-ignore lint/style/useNamingConvention: backend task_status qiymati
	bekor_qilingan: "neytral",
};

export function TasksAnalytics({ kind, data }: AnalyticsLayoutProps) {
	const { t } = useTranslation("reports");
	const bosh = t("analytics.generic.empty");
	const oddiyNom = t("analytics.tasks.notOverdue");
	const otganNom = t("analytics.tasks.overdue");
	const qatlam: Qatlam = {
		asosiy: { label: oddiyNom, color: RANG.oddiy },
		belgilangan: { label: otganNom, color: RANG.muddatiOtgan },
	};
	const qiymatMatni = (total: string, flagged: string) =>
		t("analytics.tasks.value", { total, flagged });
	const legenda = (
		<Legend
			items={[
				{ label: oddiyNom, color: RANG.oddiy },
				{ label: otganNom, color: RANG.muddatiOtgan },
			]}
		/>
	);

	const jami = data.totals.find((x) => x.key === "tasks")?.value ?? 0;
	const ustunlar = kunlikUstunlar(data.daily, qatlam, (date, base, flagged) =>
		t("analytics.tasks.dayTooltip", { date, base, flagged })
	);

	return (
		<div data-testid="tasks-analytics" className="flex flex-col gap-3.5">
			<KpiRow kind={kind} totals={data.totals} spark={data.spark} />

			<div className="grid gap-3.5 lg:grid-cols-[1.6fr_1fr]">
				<div data-widget="daily">
					<ChartCard
						title={t("analytics.tasks.dailyTitle")}
						subtitle={t("analytics.tasks.dailySubtitle")}
						extra={legenda}
					>
						<ColumnChart columns={ustunlar} height={200} empty={bosh} />
					</ChartCard>
				</div>

				<div data-widget="breakdown">
					<ChartCard title={t("analytics.tasks.statusTitle")}>
						<Donut
							segments={data.breakdown.map((b, i) => {
								const holat = HOLAT_RANGI[b.key];
								return {
									key: b.key,
									value: b.count,
									color: holat ? holatVar(holat) : kategoriyaRangi(i),
									label: t(`analytics.breakdown.${b.key}`, { defaultValue: b.key }),
								};
							})}
							centerValue={sonMatni(jami)}
							centerLabel={t("analytics.tasks.statusCenter")}
							formatValue={sonMatni}
							empty={bosh}
						/>
					</ChartCard>
				</div>
			</div>

			<div className="grid gap-3.5 lg:grid-cols-2">
				<div data-widget="assignees">
					<ChartCard
						title={t("analytics.tasks.assigneesTitle")}
						subtitle={t("analytics.tasks.assigneesSubtitle")}
						extra={legenda}
					>
						<HBarList
							empty={bosh}
							rows={segmentliQatorlar(
								data.topEmployees.slice(0, TOP_N),
								qatlam,
								(x) => x.employeeId,
								(x) => x.employeeName,
								qiymatMatni
							)}
						/>
					</ChartCard>
				</div>

				<div data-widget="departments">
					<ChartCard
						title={t("analytics.events.departmentsTitle")}
						subtitle={t("analytics.tasks.departmentsSubtitle")}
						extra={legenda}
					>
						<BolimlarRoyxati rows={data.byDepartment} qatlam={qatlam} qiymatMatni={qiymatMatni} />
					</ChartCard>
				</div>
			</div>
		</div>
	);
}
